import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import { Activity, AlertTriangle, Briefcase, Clock, CreditCard, DollarSign, Mail, TrendingUp, UserMinus } from "react-feather";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import { formatGBP, formatGBP2 } from "../../../components/Features";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { eventText, eventTone, fmtDate, fmtDateTime, Kpi, saGet, saPost, TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const NAVY = "#002962";
const RED = "#ef233c";

const SuperDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, showMessage, closeToast] = useToast();

  const { websiteData } = useWebsite();
  useSiteBranding("Platform dashboard", websiteData);

  const load = () => {
    saGet("dashboard").then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") setData(response.data.data);
    }).catch(() => setLoading(false));
  };

  useEffect(load, []);

  const resend = (inv) => {
    saPost(`invoices/${inv.id}/resend`).then((response) => {
      showMessage(response.data.message, response.data.status === "SUCCESS" ? "success" : "error");
      if (response.data.status === "SUCCESS") load();
    });
  };

  const k = data?.kpis || {};
  const attention = data?.attention || {};

  const revenueChart = {
    labels: (data?.series || []).map((s) => s.label),
    datasets: [
      { label: "Invoiced", data: (data?.series || []).map((s) => s.invoiced), backgroundColor: NAVY, borderRadius: 4, maxBarThickness: 22 },
      { label: "Collected", data: (data?.series || []).map((s) => s.collected), backgroundColor: RED, borderRadius: 4, maxBarThickness: 22 },
    ],
  };

  const adoptionChart = {
    labels: (data?.adoption || []).map((a) => a.name),
    datasets: [{ label: "Companies", data: (data?.adoption || []).map((a) => a.companies), backgroundColor: NAVY, borderRadius: 4, maxBarThickness: 16 }],
  };

  const moneyAxis = { ticks: { callback: (v) => "£" + Number(v).toLocaleString("en-GB") }, grid: { color: "#f0f2f6" }, beginAtZero: true };

  return (<div className={`${styles.Page}`}>
    <PageHeader title="Platform Dashboard" />

    {loading && <TableSkeleton kpis={6} rows={4} />}

    {!loading && data && (<>
      <div className={`${styles.KpiGrid}`}>
        <Kpi icon={<TrendingUp />} label="MRR" value={formatGBP(k.mrr)} sub={`ARR ${formatGBP(k.arr)}`} />
        <Kpi icon={<DollarSign />} label="Collected this month" value={formatGBP(k.collected_this_month)} />
        <Kpi icon={<CreditCard />} label="Outstanding" value={formatGBP(k.outstanding)} sub={`${formatGBP(k.overdue)} overdue`} />
        <Kpi icon={<AlertTriangle />} label="Overdue invoices" value={k.overdue_invoices} danger={k.overdue_invoices > 0} sub="Past due date, in grace period" />
        <Kpi icon={<Briefcase />} label="Companies" value={k.companies_total} sub={`${k.companies_active} active · ${k.companies_trial} trial`} />
        <Kpi icon={<Clock />} label="Suspended" value={k.companies_suspended} danger={k.companies_suspended > 0} />
        <Kpi icon={<UserMinus />} label="Unsubscribed" value={k.companies_cancelling} danger={k.companies_cancelling > 0} sub={`${k.companies_cancelled} ended`} />
      </div>

      <div className={`${styles.Grid2}`}>
        <div className={`${styles.Panel}`}>
          <div className={`${styles.PanelHead}`}>
            <p className={`${styles.PanelTitle}`}><TrendingUp />Revenue, last 12 months</p>
            <Link to={"/super-admin/revenue"} className={`${styles.PanelLink}`}>Revenue report →</Link>
          </div>
          <div className={`${styles.ChartBox}`}>
            <Bar data={revenueChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom" }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${formatGBP2(c.raw)}` } } }, scales: { y: moneyAxis, x: { grid: { display: false } } } }} />
          </div>
        </div>
        <div className={`${styles.Panel}`}>
          <div className={`${styles.PanelHead}`}>
            <p className={`${styles.PanelTitle}`}><Activity />Add-on adoption</p>
            <Link to={"/super-admin/addons"} className={`${styles.PanelLink}`}>Pricing →</Link>
          </div>
          <div className={`${styles.ChartBox}`}>
            <Bar data={adoptionChart} options={{ indexAxis: "y", responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "#f0f2f6" } }, y: { grid: { display: false }, ticks: { font: { size: 11 } } } } }} />
          </div>
        </div>
      </div>

      <div className={`${styles.GridHalf}`}>
        <div className={`${styles.Panel}`}>
          <div className={`${styles.PanelHead}`}>
            <p className={`${styles.PanelTitle}`}><AlertTriangle />Overdue — suspension countdown</p>
            <Link to={"/super-admin/invoices?status=overdue"} className={`${styles.PanelLink}`}>All overdue →</Link>
          </div>
          {(attention.overdue || []).length === 0 && <p className={`${styles.EmptyNote}`}>Nothing overdue.</p>}
          <ul className={`${styles.AttentionList}`}>
            {(attention.overdue || []).map((inv) => (<li key={inv.id}>
              <div>
                <Link to={`/super-admin/companies/${inv.company_id}`}>{inv.company?.company_name}</Link>
                <span className={`${styles.SubText}`}><Link to={`/super-admin/invoices/${inv.id}`}>{inv.invoice_number}</Link> · due {fmtDate(inv.due_date)}</span>
              </div>
              <div className={`${styles.AttentionRight}`}>
                <strong>{formatGBP2(inv.total)}</strong>
                <span className={`${styles.SubText} ${inv.company?.billing_status === "suspended" || inv.days_to_suspend <= 2 ? styles.Danger : ""}`}>
                  {inv.company?.billing_status === "suspended" ? "Suspended" : inv.days_to_suspend <= 0 ? "Suspends tonight" : `Suspends in ${inv.days_to_suspend} day${inv.days_to_suspend === 1 ? "" : "s"}`}
                </span>
              </div>
            </li>))}
          </ul>
        </div>

        <div className={`${styles.Panel}`}>
          <div className={`${styles.PanelHead}`}>
            <p className={`${styles.PanelTitle}`}><Clock />Invoices due in the next 3 days</p>
          </div>
          {(attention.upcoming_due || []).length === 0 && <p className={`${styles.EmptyNote}`}>No invoices due soon.</p>}
          <ul className={`${styles.AttentionList}`}>
            {(attention.upcoming_due || []).map((inv) => (<li key={inv.id}>
              <div>
                <Link to={`/super-admin/companies/${inv.company_id}`}>{inv.company?.company_name}</Link>
                <span className={`${styles.SubText}`}><Link to={`/super-admin/invoices/${inv.id}`}>{inv.invoice_number}</Link></span>
              </div>
              <div className={`${styles.AttentionRight}`}>
                <strong>{formatGBP2(inv.total)}</strong>
                <span className={`${styles.SubText}`}>due {fmtDate(inv.due_date)}</span>
              </div>
            </li>))}
          </ul>
        </div>
      </div>

      <div className={`${styles.GridHalf}`}>
        <div className={`${styles.Panel}`}>
          <div className={`${styles.PanelHead}`}>
            <p className={`${styles.PanelTitle}`}><Briefcase />Trials ending this week</p>
          </div>
          {(attention.trials_ending || []).length === 0 && <p className={`${styles.EmptyNote}`}>No trials ending in the next 7 days.</p>}
          <ul className={`${styles.AttentionList}`}>
            {(attention.trials_ending || []).map((c) => (<li key={c.id}>
              <div>
                <Link to={`/super-admin/companies/${c.id}`}>{c.company_name}</Link>
                <span className={`${styles.SubText}`}>{c.email}</span>
              </div>
              <div className={`${styles.AttentionRight}`}>
                <span className={`${styles.SubText}`}>ends {fmtDate(c.trial_ends_at)}</span>
                <span className={`${styles.SubText}`}>{dayjs(c.trial_ends_at).diff(dayjs().startOf("day"), "day")} days left</span>
              </div>
            </li>))}
          </ul>
        </div>

        <div className={`${styles.Panel}`}>
          <div className={`${styles.PanelHead}`}>
            <p className={`${styles.PanelTitle}`}><Mail />Failed invoice emails</p>
          </div>
          {(attention.failed_emails || []).length === 0 && <p className={`${styles.EmptyNote}`}>All invoices were delivered.</p>}
          <ul className={`${styles.AttentionList}`}>
            {(attention.failed_emails || []).map((inv) => (<li key={inv.id}>
              <div>
                <Link to={`/super-admin/invoices/${inv.id}`}>{inv.invoice_number}</Link>
                <span className={`${styles.SubText}`} title={inv.email_error}>{inv.company?.company_name}</span>
              </div>
              <div className={`${styles.AttentionRight}`}>
                <button type="button" className={`${styles.ActionBU}`} onClick={() => resend(inv)}>Resend</button>
              </div>
            </li>))}
          </ul>
        </div>
      </div>

      <div className={`${styles.Panel}`} style={{ marginBottom: 18 }}>
        <div className={`${styles.PanelHead}`}>
          <p className={`${styles.PanelTitle}`}><UserMinus />Cancelling in the next 14 days</p>
          <Link to={"/super-admin/companies?status=cancelling"} className={`${styles.PanelLink}`}>All unsubscribed →</Link>
        </div>
        {(attention.cancelling_soon || []).length === 0 && <p className={`${styles.EmptyNote}`}>No companies are leaving soon.</p>}
        <ul className={`${styles.AttentionList}`}>
          {(attention.cancelling_soon || []).map((c) => (<li key={c.id}>
            <div>
              <Link to={`/super-admin/companies/${c.id}`}>{c.company_name}</Link>
              <span className={`${styles.SubText}`}>{c.cancel_reason || "No reason given"}</span>
            </div>
            <div className={`${styles.AttentionRight}`}>
              <span className={`${styles.SubText} ${styles.Danger}`}>Ends {fmtDate(c.cancels_at)}</span>
              <span className={`${styles.SubText}`}>{c.billing_status === "trial" ? "In trial" : "Paying"}</span>
            </div>
          </li>))}
        </ul>
      </div>

      <div className={`${styles.Panel}`}>
        <div className={`${styles.PanelHead}`}>
          <p className={`${styles.PanelTitle}`}><Activity />Recent billing activity</p>
          <Link to={"/super-admin/activity"} className={`${styles.PanelLink}`}>Activity log →</Link>
        </div>
        {(data?.activity || []).length === 0 && <p className={`${styles.EmptyNote}`}>No activity yet.</p>}
        <ul className={`${styles.Timeline}`}>
          {(data?.activity || []).map((e) => (<li key={e.id} className={eventTone(e.type)}>
            <span className={`${styles.TimelineText}`}>{eventText(e)}</span>
            <span className={`${styles.SubText}`}>
              {e.company_id ? <Link to={`/super-admin/companies/${e.company_id}`}>{e.company_name}</Link> : "Platform"} · {e.actor_name || "System"} · {fmtDateTime(e.created_at)}
            </span>
          </li>))}
        </ul>
      </div>
    </>)}

    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(SuperDashboard);
